import { useEffect, useState, type ChangeEvent } from 'react';
import { Badge, Button, Card, Field, Switch } from '../../components/kit';
import { PageHead } from '../../components/list';
import { bankMeta } from '../../components/format';
import { bankLogo } from '../../components/bankLogos';
import { adminApi, type AdminBank } from '../../api/admin';
import { useBranding } from '../../branding/BrandingProvider';
import '../page.css';
import './admin.css';

/** Yayın adresinden (domain) bankanın callback/dönüş adresini türetir. */
function callbackBase(domain: string): string {
  const d = domain.trim().replace(/^https?:\/\//, '').replace(/\/+$/, '');
  return d ? `https://${d}` : window.location.origin;
}

/** Her banka için kimlik alanları (eski site_ayar sütunlarına göre). */
const BANK_FIELDS: Record<string, { key: string; label: string }[]> = {
  qnb: [
    { key: 'merchant_id', label: 'Merchant ID' },
    { key: 'merchant_pass', label: 'Merchant Pass' },
    { key: 'user_code', label: 'User Code' },
    { key: 'user_pass', label: 'User Pass' },
    { key: 'mbr_id', label: 'Mbr ID' },
  ],
  yapikredi: [
    { key: 'merchant_id', label: 'Merchant ID' },
    { key: 'terminal_id', label: 'Terminal ID' },
    { key: 'posnet_id', label: 'Posnet ID' },
    { key: 'enc_key', label: 'Enc Key' },
  ],
  akbank: [
    { key: 'merchant_safe_id', label: 'Merchant Safe ID (Güvenli İşyeri No)' },
    { key: 'terminal_safe_id', label: 'Terminal Safe ID' },
    { key: 'secret_key', label: 'Secret Key (Güvenlik Anahtarı)' },
  ],
  isbankasi: [
    { key: 'merchant_id', label: 'Merchant ID' },
    { key: 'store_key', label: 'Store Key' },
  ],
  garanti: [
    { key: 'merchant_id', label: 'Merchant ID' },
    { key: 'terminal_id', label: 'Terminal ID' },
    { key: 'prov_user', label: 'Prov User' },
    { key: 'prov_pass', label: 'Prov Pass' },
    { key: 'store_key', label: 'Store Key' },
  ],
};

/** Gizli tutulması gereken kimlik alanları (şifre / anahtar / gizli anahtar). */
const SENSITIVE = /pass|key|secret/i;

const ratesToStr = (r: unknown): string => (Array.isArray(r) ? r.join('|') : '');
const strToRates = (s: string): number[] =>
  s.split('|').map((x) => Number(x.trim())).filter((n) => !Number.isNaN(n));

/** Maskeli kimlik alanı — varsayılan gizli, Göster/Gizle ile açılır. */
function SecretField({ label, id, value, onChange }: {
  label: string; id: string; value: string; onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="secret-row">
        <input id={id} className="input" type={show ? 'text' : 'password'} value={value}
          autoComplete="off" spellCheck={false} onChange={onChange} />
        <button type="button" className="secret-toggle" onClick={() => setShow((s) => !s)}>
          {show ? 'Gizle' : 'Göster'}
        </button>
      </div>
    </div>
  );
}

function BankEditor({ bank, onSaved }: { bank: AdminBank; onSaved: () => void }) {
  const { branding } = useBranding();
  const cfg = bank.config as any;
  const meta = bankMeta(bank.key);
  const logo = bankLogo(bank.key);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const callbackUrl = `${callbackBase(branding.domain)}/api/payments/callback/${bank.key}`;
  const copyCallback = () => {
    navigator.clipboard?.writeText(callbackUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };
  const [environment, setEnvironment] = useState(bank.environment);
  const [securityType, setSecurityType] = useState(bank.securityType);
  const [creds, setCreds] = useState<Record<string, string>>({ ...(cfg.credentials ?? {}) });
  const [currency, setCurrency] = useState<string>(cfg.currency ?? '949');
  const [rates, setRates] = useState<string>(ratesToStr(cfg.installments?.rates));
  const [hsbcRates, setHsbcRates] = useState<string>(ratesToStr(cfg.installments?.hsbc_rates));
  const [gwTest, setGwTest] = useState<string>(cfg.gateway?.test ?? '');
  const [gwProd, setGwProd] = useState<string>(cfg.gateway?.production ?? '');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const fields = BANK_FIELDS[bank.key] ?? [{ key: 'merchant_id', label: 'Merchant ID' }];

  const toggleEnabled = async () => {
    await adminApi.updateBank(bank.key, { enabled: !bank.enabled });
    onSaved();
  };

  const save = async () => {
    setMsg(null);
    setBusy(true);
    const config = {
      ...cfg,
      credentials: { ...(cfg.credentials ?? {}), ...creds },
      currency,
      gateway: { test: gwTest, production: gwProd },
      installments: {
        ...(cfg.installments ?? { enabled: true, max: 6 }),
        rates: strToRates(rates),
        ...(bank.key === 'qnb' ? { hsbc_rates: strToRates(hsbcRates) } : {}),
      },
    };
    try {
      await adminApi.updateBank(bank.key, { environment, securityType, config });
      setMsg('Kaydedildi');
      onSaved();
    } catch {
      setMsg('Kaydedilemedi');
    } finally {
      setBusy(false);
    }
  };

  const setCred = (key: string) => (e: ChangeEvent<HTMLInputElement>) =>
    setCreds((c) => ({ ...c, [key]: e.target.value }));

  return (
    <Card className="bank-card">
      <div className="bank-head">
        {logo ? (
          <span className="bank-logo-chip"><img src={logo} alt={bank.label} /></span>
        ) : (
          <span className="bank-ava" style={{ background: meta.color }}>{meta.ini}</span>
        )}
        <div className="bank-id">
          <strong>{bank.label}</strong>
          <div className="bank-badges">
            <Badge tone={bank.environment === 'production' ? 'accent' : 'default'}>{bank.environment}</Badge>
            <Badge tone="default">{bank.securityType}</Badge>
            <Badge tone={bank.enabled ? 'good' : 'bad'} dot>{bank.enabled ? 'Aktif' : 'Kapalı'}</Badge>
          </div>
        </div>
        <span className="grow" />
        <Switch checked={bank.enabled} onChange={toggleEnabled} label={bank.enabled ? 'Aktif' : 'Kapalı'} />
        <Button size="sm" variant="secondary" onClick={() => setOpen((o) => !o)}>{open ? 'Kapat' : 'Düzenle'}</Button>
      </div>

      {open && (
        <div className="bank-editor">
          <div className="bank-section-title">Ortam &amp; Güvenlik</div>
          <div className="inline-form">
            <div className="field">
              <label>Ortam</label>
              <select className="input" value={environment} onChange={(e) => setEnvironment(e.target.value as any)}>
                <option value="test">test</option>
                <option value="production">production</option>
              </select>
            </div>
            <div className="field">
              <label>Güvenlik Tipi</label>
              <select className="input" value={securityType} onChange={(e) => setSecurityType(e.target.value)}>
                <option value="3DPay">3DPay</option>
                <option value="3DHost">3DHost</option>
                <option value="3DModel">3DModel</option>
                <option value="NonSecure">NonSecure</option>
              </select>
            </div>
          </div>

          <div className="bank-section-title">Kimlik Bilgileri</div>
          <div className="inline-form">
            {fields.map((f) =>
              SENSITIVE.test(f.key) ? (
                <SecretField key={f.key} label={f.label} id={`${bank.key}-${f.key}`}
                  value={creds[f.key] ?? ''} onChange={setCred(f.key)} />
              ) : (
                <Field key={f.key} label={f.label} id={`${bank.key}-${f.key}`}
                  value={creds[f.key] ?? ''} onChange={setCred(f.key)} />
              ),
            )}
            <Field label="Para Birimi (949=TL, 840=USD)" id={`${bank.key}-cur`} value={currency} onChange={(e) => setCurrency(e.target.value)} />
          </div>

          <div className="bank-section-title">Taksit Oranları</div>
          <div className="field">
            <label>Taksit Oranları</label>
            <input className="input" value={rates} onChange={(e) => setRates(e.target.value)} placeholder="0|0|0|0|0|0" />
            <span className="pay-note">En fazla 12 oran. Ondalık için nokta, oranlar arası “|”. Örn: 1.1|2.2|3.3|4.4|5.4|6.6</span>
          </div>
          {bank.key === 'qnb' && (
            <div className="field">
              <label>HSBC Taksit Oranları</label>
              <input className="input" value={hsbcRates} onChange={(e) => setHsbcRates(e.target.value)} placeholder="0|0|0|0|0|0" />
            </div>
          )}

          <div className="bank-section-title">Gateway &amp; Callback</div>
          <div className="inline-form">
            <Field label="Gateway (test)" id={`${bank.key}-gt`} value={gwTest} onChange={(e) => setGwTest(e.target.value)} />
            <Field label="Gateway (production)" id={`${bank.key}-gp`} value={gwProd} onChange={(e) => setGwProd(e.target.value)} />
          </div>
          <div className="field">
            <label>Callback / Dönüş Adresi (bankaya tanımlanacak)</label>
            <div className="callback-row">
              <input className="input" readOnly value={callbackUrl} onFocus={(e) => e.target.select()} />
              <Button size="sm" variant="secondary" type="button" onClick={copyCallback}>
                {copied ? '✓ Kopyalandı' : 'Kopyala'}
              </Button>
            </div>
            <span className="pay-note">
              Otomatik üretilir — “Yayın Adresi (Domain)”nden türer, düzenlenmez. 3D ödeme sonrası banka bu adrese döner
              (okUrl/failUrl/callback aynı). Bankanın sanal POS panelinde bu adresi tanımlayın.
              {!branding.domain && ' Not: Site Ayarları’ndan yayın domainini girince gerçek adres oluşur.'}
            </span>
          </div>

          <div className="bank-actions">
            <Button onClick={save} disabled={busy}>{busy ? 'Kaydediliyor…' : 'Kaydet'}</Button>
            {msg && <span style={{ color: msg === 'Kaydedildi' ? 'var(--good)' : 'var(--bad)', fontSize: 13 }}>{msg}</span>}
          </div>
        </div>
      )}
    </Card>
  );
}

export function Banks() {
  const [banks, setBanks] = useState<AdminBank[]>([]);
  const load = () => adminApi.banks().then((r) => setBanks(r.banks));
  useEffect(() => { load(); }, []);

  const active = banks.filter((b) => b.enabled).length;

  return (
    <div className="page">
      <PageHead
        eyebrow="Ayarlar"
        title="Banka Ayarları"
        sub="Bankaları açıp kapatın, ortam (test/prod) ve kimlik bilgilerini yönetin. Güvenlik için taşınan bankalar kapalıdır; gerçek kimliklerle test edip açın."
        right={banks.length > 0 ? <span className="lt-count">{active}/{banks.length} banka aktif</span> : undefined}
      />
      {banks.map((b) => <BankEditor key={b.key} bank={b} onSaved={load} />)}
    </div>
  );
}
